import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { currencyOptions } from '../../../../../../utils/constants/currencyOptions';
import { useCanvas } from '../../../../context/CanvasContext';
import {
  fetchAccounts,
  selectAccounts,
  selectAccountsLoading,
  updateAccount,
  deleteAccount,
  uploadAccounts,
  createAccount,
  selectAccountsPagination,
} from '../../../../../../app/store/slices/accountSlice';
import {updateCanvas, fetchUserCanvases} from '../../../../../../app/store/slices/canvasSlice' 
import ConfirmationModal from '../../../../../shared/components/modals/ConfirmationModal';
import EditModal from '../../../../../shared/components/modals/EditModal';
import axios from 'axios';
import {FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Pagination from '../../../../../shared/components/Pagination';
import TableHeader from '../../../../../shared/components/TableHeader';
import FileUploadButton from '../../../../../shared/components/FileUploadButton';
import ActionButtons from '../../../../../shared/components/ActionButton';
import TableBody from '../../../../../shared/components/TableBody';
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import {PAGE_SIZE_OPTIONS, accountTableHeader} from '../../../../../../utils/constants/dashboardConstants'
import EditableCell from '../../../../../shared/components/EditableCell';
import InstructionsModal from '../../../../../shared/components/InstructionsModal';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { MdAccountBalanceWallet } from "react-icons/md";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import {  fetchUserInsights
} from '../../../../../../app/store/slices/userDashboardSlice';
import {accountTypes} from '../../../../../../utils/constants/dashboardConstants';
import  {formatAmount } from '../../../../../../utils/formatAmount'





const AccountsContent = () => {

  const dispatch = useDispatch();
  const {selectedCanvas, setSelectedCanvas } = useCanvas();
  const accounts = useSelector(selectAccounts);
  const loading = useSelector(selectAccountsLoading);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editAccountData, setEditAccountData] = useState({ name: '', type: '', currency: '' });
  const [addAccountData, setAddAccountData] = useState({ name: '', type: '', currency: '', balance: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [totalInBase, setTotalInBase] = useState(0);
  const [conversionRates, setConversionRates] = useState({});
  const [accountsWithBase, setAccountsWithBase] = useState([]);
  const fileInputRef = useRef();
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [selectedBaseCurrency, setSelectedBaseCurrency] = useState(selectedCanvas?.currency || '');
  const pagination = useSelector(selectAccountsPagination);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const addOptionsRef = useRef(null);
  const [editingBalanceId, setEditingBalanceId] = useState(null);
  const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const insights = useSelector(state => state.userDashboard.insights);
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  useEffect(() => {
    if (selectedCanvas?.id) {
      if (selectedCanvas.currency) {
        setSelectedBaseCurrency(selectedCanvas.currency);
      }
      dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
    }
  }, [dispatch, selectedCanvas?.id, selectedCanvas?.currency, currentPage, pageSize]);

  useEffect(() => {
    if (showEditForm && selectedAccount) {
      setEditAccountData({
        name: selectedAccount.name,
        type: selectedAccount.type,
        currency: selectedAccount.currency,
      });
    }
  }, [showEditForm, selectedAccount]);

  useEffect(() => {
    const fetchRates = async () => {
      if (!selectedBaseCurrency || !accounts?.length) {
        setConversionRates({});
        setTotalInBase(0);
        setAccountsWithBase([]);
        return;
      }
      const uniqueCurrencies = [
        ...new Set(accounts.map(acc => acc.currency).filter(cur => cur && cur !== selectedBaseCurrency))
      ];
      if (uniqueCurrencies.length === 0) {
        const accountsBase = accounts.map(acc => ({
          ...acc,
          baseBalance: Number(acc.balance) || 0,
        }));
        const total = accountsBase.reduce((sum, acc) => sum + acc.baseBalance, 0);
        setAccountsWithBase(accountsBase);
        setTotalInBase(total);
        setConversionRates({});
        return;
      }
      try {
        const res = await axios.get(
          `https://api.exchangerate-api.com/v4/latest/${selectedBaseCurrency}`,
        );
        const rates = res.data.rates || {};
        setConversionRates(rates);

        let total = 0;
        const accountsBase = accounts.map(acc => {
          const bal = Number(acc.balance) || 0;
          let baseBalance = 0;
          if (acc.currency === selectedBaseCurrency) {
            baseBalance = bal;
          } else if (rates[acc.currency]) {
            baseBalance = bal * (1 / rates[acc.currency]);
          } else {
            baseBalance = 0;
          }
          total += baseBalance;
          return { ...acc, baseBalance };
        });
        setAccountsWithBase(accountsBase);
        setTotalInBase(total);
      } catch (err) {
        setTotalInBase(0);
        setConversionRates({});
        setAccountsWithBase([]);
      }
    };
    fetchRates();
  }, [accounts, selectedBaseCurrency]);

  const handleBaseCurrencyChange = async (newCurrency) => {
  if (newCurrency === selectedBaseCurrency) return;
  setSelectedBaseCurrency(newCurrency);
  try {
    await dispatch(updateCanvas({ 
      id: selectedCanvas.id, 
      currency: newCurrency 
    })).unwrap();

    await dispatch(fetchAccounts({ 
      canvasId: selectedCanvas.id, 
      page: currentPage, 
      limit: pageSize 
    })).unwrap()
    dispatch(fetchUserCanvases()).unwrap( )
    dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
    dispatch(fetchUserInsights(selectedCanvas?.id));
    toast.success('Base Currency Changed Successfully');
  } catch (error) {
    toast.error('Failed to update base currency');
  }
};

  // Handle Delete
  const handleDeleteAccount = async () => {
    if (!selectedAccount || !selectedCanvas?.id) return;
    setIsDeleting(true);
    try {
      await dispatch(
        deleteAccount({
          canvasId: selectedCanvas.id,
          accountId: selectedAccount._id,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
      dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Account Delected Successfully')
    } catch (err) {
      toast.error(err);
      throw new Error(err)
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditAccount = async (e) => {
    e.preventDefault();
    if (!selectedAccount || !selectedCanvas?.id) return;
    setIsEditing(true);
    try {
      await dispatch(
        updateAccount({
          canvasId: selectedCanvas.id,
          accountId: selectedAccount._id,
          updateData: editAccountData,
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedAccount(null);
      dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Account Updated Successfully')
    } catch (err) {
      toast.error(err)
      throw new Error(err)
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add Account
  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) return;
    setIsAdding(true);
    try {
      await dispatch(
        createAccount({
          canvasId: selectedCanvas.id,
          accountData: {
            ...addAccountData,
            balance: Number(addAccountData.balance) || 0,
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddAccountData({ name: '', type: '', currency: '', balance: '' });
      dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Account Added Successfully');
    } catch (err) {
      toast.error(err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    if (file) {
    
      try {
      await dispatch(uploadAccounts({ canvasId: selectedCanvas?.id, file })).unwrap();
      dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Accounts Added Successfully')

      } catch (err) {
        toast.error(err)
        console.error('Upload failed:', err);
      }
    }
  };

  const handleBalanceClick = (account) => {
    setEditingBalanceId(account._id);
  };

  const handleSaveBalance = async (account, newValue) => {
    setIsUpdatingBalance(true);
    try {
      await dispatch(
        updateAccount({
          canvasId: selectedCanvas.id,
          accountId: account._id,
          updateData: {
            name: account.name,
            type: account.type,
            currency: account.currency,
            balance: Number(newValue) || 0,
          },
        })
      ).unwrap();

      setEditingBalanceId(null);
      await dispatch(fetchAccounts({ canvasId: selectedCanvas?.id, page: currentPage, limit: pageSize }));
      await dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Balance updated successfully');
    } catch (err) {
      toast.error('Failed to update balance');
    } finally {
      setIsUpdatingBalance(false);
    }
  };

  const handleDiscardBalance = () => {
    setEditingBalanceId(null);
  };

  const handleDownloadTemplate = () => {
    const AccountsTemplateData = [
      { name: 'Main Checking', type: 'Current', currency: 'USD', balance: '5000.00' },
      { name: 'Savings Account', type: 'Savings', currency: 'USD', balance: '15000.50' },
      { name: 'Investment Portfolio', type: 'Investment', currency: 'EUR', balance: '8500.75' }
    ];

    const csvContent = [
      'name,type,currency,balance',
      ...AccountsTemplateData.map(row => `${row.name},${row.type},${row.currency},${row.balance}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'accounts_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);

    setShowInstructions(false);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Accounts</h1>
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        {/* Header Section */}
       <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
          {/* Cards Row */}
          <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto"> 

            
            {/* Base Currency */}
            <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow border min-w-[180px] w-full sm:w-auto">
              <div className="flex-shrink-0 bg-blue-100 text-btn-primary rounded-full p-2 mr-3">
                <FiDollarSign className='h-5 w-5'/>
              </div>
              <div>
                <div className="text-xs text-gray-500 font-medium">Base Currency</div>
                <div>
                  <select
                    className="ml-0 px-3 py-0.5 rounded border focus:ring-2 focus:ring-btn-primary focus:border-transparent bg-white text-lg font-bold text-btn-primary leading-tight"
                    value={selectedBaseCurrency}
                    onChange={e => handleBaseCurrencyChange(e.target.value)}
                  >
                    <option value="">Select currency</option>
                    {currencyOptions.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - ({currency.symbol})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Total Balance */}
            <SummaryCard
              title="Total Balance"
              value={
                selectedBaseCurrency
                  ? `${formatAmount(insights?.totalBalance)} ${selectedBaseCurrency}`
                  : 'Auto calculated'
              }
              icon={<MdAccountBalanceWallet className="w-5 h-5" />}
              valueColor="text-green-700"
              iconBg="bg-blue-100"
              iconColor="text-green-600"
              formatValue={val => val}
              minWidth="min-w-[140px] md:min-w-[180px]"
            />
          </div>
          {/* Add Account Button */}
        <AddOptionsButton
            showOptions={showAddOptions}
            setShowOptions={setShowAddOptions}
            addOptionsRef={addOptionsRef}
            onAddManual={() => setShowAddForm(true)}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            onShowInstructions={() => setShowInstructions(true)}
            buttonLabel="Add Account"
            buttonTitle="Add Account"
            manualLabel="Add Manually"
            FileUploadButtonComponent={FileUploadButton}
          />
        </div>
        {/* Table Section */}
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
               <TableHeader columns ={accountTableHeader}/>
               <TableBody
                  loading={loading}
                  data={accountsWithBase}
                  columnsCount={6}
                  emptyMessage="No Account Found."
                  loadingMessage="Loading Accounts..."
                  renderRow={account => (
                    <tr key={account._id}>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{account.name}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {
                          (accountTypes.find(t => t.value === account.type)?.label) ||
                          account.type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                        }
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{account.currency}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <EditableCell
                          value={account.balance}
                          isEditing={editingBalanceId === account._id}
                          isUpdating={isUpdatingBalance}
                          onEdit={() => handleBalanceClick(account)}
                          onSave={(newValue) => handleSaveBalance(account, newValue)}
                          onCancel={handleDiscardBalance}
                          inputType="number"
                          inputProps={{
                            min: "0",
                            step: "0.01"
                          }}
                          displayFormat={(val) => `${account.currency} ${formatAmount(val)}`}
                          editTitle="Click to edit balance"
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.baseBalance !== undefined
                          ? `${selectedBaseCurrency} ${formatAmount(account?.baseBalance)}`
                          : '-'}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                         <ActionButtons
                          onEdit={() => {
                            setSelectedAccount(account);
                            setShowEditForm(true);
                          }}
                          onDelete={() => {
                            setSelectedAccount(account);
                            setShowDeleteConfirm(true);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                />
              </table>
             <Pagination
                pagination={pagination}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
                label="Accounts per page"
                totalLabel="total Accounts"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Account"
        message={`Are you sure you want to delete "${selectedAccount?.name}"? All your transactions and related data will be permanently deleted.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedAccount(null);
        }}
        onConfirm={handleDeleteAccount}
        loading={isDeleting}
      />

      {/* Edit Modal */}
      <EditModal
        show={showEditForm}
        title="Edit Account"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedAccount(null);
        }}
        onSubmit={handleEditAccount}
        loading={isEditing}
        submitText='Save'
      >
        <div className="space-y-4">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Account Name
            </label>
            <input
              type="text"
              value={editAccountData.name}
              onChange={e => setEditAccountData({ ...editAccountData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={editAccountData.type}
              onChange={e => setEditAccountData({ ...editAccountData, type: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Type</option>
              {accountTypes.map((type) => (
                  <option key={type.key} value={type.value} className="capitalize">
                    {type.label}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </EditModal>

      {/* Add Account Modal */}
      <EditModal
        show={showAddForm}
        title="Add Account"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddAccount}
        submitText='Save'
        loading={isAdding}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Account Name
            </label>
            <input
              type="text"
              value={addAccountData.name}
              onChange={e => setAddAccountData({ ...addAccountData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={addAccountData.type}
              onChange={e => setAddAccountData({ ...addAccountData, type: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Type</option>
               {accountTypes.map((type) => (
                  <option key={type.key} value={type.value} className="capitalize">
                    {type.label}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              value={addAccountData.currency}
              onChange={e => setAddAccountData({ ...addAccountData, currency: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Currency</option>
              {currencyOptions.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.symbol} ({currency.name})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">
              Balance
            </label>
            <input
              type="number"
              value={addAccountData.balance}
              onChange={e => setAddAccountData({ ...addAccountData, balance: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </EditModal>

      {/* Instructions Modal */}
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="accounts"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default AccountsContent;